import React, { useEffect, useRef, useState } from 'react';
const CartIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M3.75 5.25H6.25L8.45 14.1C8.6 14.7 9.14 15.12 9.76 15.12H18.18C18.76 15.12 19.28 14.75 19.47 14.2L21 9.48H7.18"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
        />
        <circle cx="10.25" cy="19" r="1.45" fill="currentColor" />
        <circle cx="17.5" cy="19" r="1.45" fill="currentColor" />
    </svg>
);

const TrashIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M9 3.75H15M4.5 6.75H19.5M18 6.75L17.42 18.33C17.37 19.38 16.5 20.25 15.45 20.25H8.55C7.5 20.25 6.63 19.38 6.58 18.33L6 6.75M10 10.5V16.5M14 10.5V16.5"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
        />
    </svg>
);

const VERTICAL_ICON_CLASS_NAMES = {
    '3D': 'glyphicon-cube',
    GRA: 'glyphicon-image',
    SFX: 'glyphicon-music',
    VFX: 'glyphicon-film',
};

const CART_PREVIEW_IMAGE_BY_VERTICAL = {
    '3D': '/images/marketplace-cart-preview-3d.svg',
    GRA: '/images/marketplace-cart-preview-graphics.svg',
    SFX: '/images/marketplace-cart-preview-audio.svg',
    VFX: '/images/marketplace-cart-preview-vfx.svg',
};

const DEFAULT_CART_PREVIEW_IMAGE = CART_PREVIEW_IMAGE_BY_VERTICAL.VFX;

const getDisplayName = (line) => {
    const rawName = String(line.display?.name || 'Untitled item');
    return rawName.replace(/^\(4k\)\s*/i, '').trim() || 'Untitled item';
};

const getDisplayVertical = (line) => String(line.display?.vertical ?? '').trim().toUpperCase();

const getDisplayVerticalIconClassName = (vertical) => VERTICAL_ICON_CLASS_NAMES[vertical] || '';

const getCartLinePreviewImage = (line, vertical) => line.display?.previewUrl
    || CART_PREVIEW_IMAGE_BY_VERTICAL[vertical]
    || DEFAULT_CART_PREVIEW_IMAGE;

const extractWaveformValues = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload.data)) {
        return payload.data;
    }
    if (Array.isArray(payload.samples)) {
        return payload.samples;
    }
    if (Array.isArray(payload.values)) {
        return payload.values;
    }
    if (Array.isArray(payload.waveform)) {
        return payload.waveform;
    }
    return [];
};

const getWaveformKey = (line) => `${line.lineKey}:${line.display?.waveformUrl || ''}`;

const CartWaveformPreview = ({ values = [] }) => {
    const barsToShow = 36;
    const maxBarHeight = 34;
    const minBarHeight = 4;
    const fallbackBars = [0.1, 0.16, 0.12, 0.2, 0.28, 0.22, 0.42, 0.68, 0.48, 0.32, 0.2, 0.14, 0.1];
    const sourceValues = values.length ? values : fallbackBars;
    const chunkSize = Math.max(Math.floor(sourceValues.length / barsToShow), 1);
    const bars = [];

    for (let index = 0; index < barsToShow; index += 1) {
        const start = index * chunkSize;
        const slice = sourceValues.slice(start, start + chunkSize);
        if (!slice.length) {
            break;
        }
        const average = slice.reduce((total, value) => total + Math.abs(Number(value) || 0), 0) / slice.length;
        bars.push(average);
    }

    const max = bars.reduce((currentMax, value) => Math.max(currentMax, value), 0) || 1;

    return (
        <div className={`marketplace-cart-line__waveform ${values.length ? '' : 'is-loading'}`.trim()}>
            {bars.map((value, index) => (
                <span
                    key={`marketplace-cart-waveform-${index}`}
                    style={{ height: `${Math.max((value / max) * maxBarHeight, minBarHeight)}px` }}
                />
            ))}
        </div>
    );
};

const formatCartLinePrice = (line) => {
    const amount = Number(line.display?.amount);
    const currency = String(line.display?.currency ?? '').trim().toUpperCase();

    if (Number.isFinite(amount) && currency) {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        } catch (error) {
            return `${currency} ${Math.round(amount)}`;
        }
    }

    const priceLabel = String(line.display?.priceLabel ?? '').trim();
    return priceLabel ? priceLabel.replace(/([.,]\d{2})(?!.*\d)/, '') : 'Pending';
};

const dispatchAssetOpenEvent = ({ assetOpenEventName, line }) => {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function' || typeof window.dispatchEvent !== 'function') {
        return false;
    }

    const assetOpenEvent = new window.CustomEvent(assetOpenEventName, {
        cancelable: true,
        detail: {
            assetHref: line.assetHref || null,
            assetUuid: line.assetUuid || null,
            lineKey: line.lineKey,
            source: 'marketplace-cart',
        },
    });

    const dispatchResult = window.dispatchEvent(assetOpenEvent);
    return assetOpenEvent.defaultPrevented || dispatchResult === false;
};

const MarketplaceCartSummary = ({
    snapshot,
    embedded = false,
    checkoutPending = false,
    onCheckout,
    onRemove,
    showCheckoutAuthPrompt = false,
    loginHref = '/login?return_to=/marketplace/cart&cart_intent=checkout',
    signupHref = '/register',
    subscribeHref = '/upgrade',
    subscribeLabel = 'Unlimited access',
    subscribeSubtext = 'Save with a Pro Plan and access the entire library',
    continueExploringHref = null,
    assetInteractionMode = 'event-first',
    assetOpenEventName = 'crate:asset:open',
}) => {
    const [waveforms, setWaveforms] = useState({});
    const requestedWaveforms = useRef(new Set());

    useEffect(() => {
        if (typeof fetch !== 'function') {
            return undefined;
        }

        let isCancelled = false;
        snapshot.lines.forEach((line) => {
            const waveformUrl = line.display?.waveformUrl;
            if (!waveformUrl) {
                return;
            }

            const waveformKey = getWaveformKey(line);
            if (requestedWaveforms.current.has(waveformKey)) {
                return;
            }

            requestedWaveforms.current.add(waveformKey);
            fetch(waveformUrl)
                .then((response) => response.json())
                .then((payload) => {
                    if (isCancelled) {
                        return;
                    }

                    setWaveforms((currentWaveforms) => ({
                        ...currentWaveforms,
                        [waveformKey]: extractWaveformValues(payload),
                    }));
                })
                .catch(() => {
                    if (isCancelled) {
                        return;
                    }

                    setWaveforms((currentWaveforms) => ({
                        ...currentWaveforms,
                        [waveformKey]: [],
                    }));
                });
        });

        return () => {
            isCancelled = true;
        };
    }, [snapshot.lines]);

    const handleAssetClick = (line) => {
        if (assetInteractionMode === 'link-only') {
            return;
        }

        const wasHandled = dispatchAssetOpenEvent({
            assetOpenEventName,
            line,
        });

        if (!wasHandled && line.assetHref && typeof window !== 'undefined' && window.location?.assign) {
            window.location.assign(line.assetHref);
        }
    };

    const renderAssetAction = ({ className, line, content, label = null }) => {
        const canOpenAsset = line.type === 'asset' && !!line.assetHref;
        const shouldDispatchAssetEvent = line.type === 'asset' && assetInteractionMode === 'event-first' && canOpenAsset;
        const shouldRenderAssetLink = line.type === 'asset' && assetInteractionMode === 'link-only' && canOpenAsset;

        if (shouldRenderAssetLink) {
            return (
                <a aria-label={label || undefined} className={className} href={line.assetHref}>
                    {content}
                </a>
            );
        }

        if (shouldDispatchAssetEvent) {
            return (
                <button
                    aria-label={label || undefined}
                    className={className}
                    onClick={() => handleAssetClick(line)}
                    type="button"
                >
                    {content}
                </button>
            );
        }

        return (
            <span className={className}>
                {content}
            </span>
        );
    };

    if (!snapshot.lines.length) {
        return (
            <div className={`marketplace-cart-summary ${embedded ? 'is-embedded' : ''}`}>
                <div className="marketplace-cart-empty">
                    <div className="marketplace-cart-empty__icon">
                        <CartIcon />
                    </div>
                    <h2>Your cart is empty</h2>
                    <p>Add one-time assets or bundles, then come back here to complete checkout.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`marketplace-cart-summary ${embedded ? 'is-embedded' : ''}`}>
            <div className="marketplace-cart-summary__items">
                {snapshot.lines.map((line) => {
                    const displayName = getDisplayName(line);
                    const displayVertical = getDisplayVertical(line);
                    const displayVerticalIconClassName = getDisplayVerticalIconClassName(displayVertical);
                    const previewImage = getCartLinePreviewImage(line, displayVertical);
                    const shouldRenderWaveform = displayVertical === 'SFX' && !!line.display?.waveformUrl;
                    const waveformKey = getWaveformKey(line);
                    const nameContent = (
                        <>
                            {displayVertical &&
                            <span className="marketplace-cart-line__vertical" title={displayVertical}>
                                {displayVerticalIconClassName &&
                                <span className={`glyphicon ${displayVerticalIconClassName}`} aria-hidden="true" />
                                }
                                {!displayVerticalIconClassName &&
                                <span aria-hidden="true">{displayVertical}</span>
                                }
                                <span className="screen-reader-only">{displayVertical}</span>
                            </span>
                            }
                            <span className="marketplace-cart-line__name-text" title={displayName}>{displayName}</span>
                        </>
                    );
                    const previewContent = shouldRenderWaveform
                        ? <CartWaveformPreview values={waveforms[waveformKey] || []} />
                        : <img className="marketplace-cart-line__preview-image" src={previewImage} alt="" loading="lazy" />;

                    return (
                        <article className="marketplace-cart-line" key={line.lineKey}>
                            {renderAssetAction({
                                className: 'marketplace-cart-line__preview-action',
                                line,
                                label: `Open preview for ${displayName}`,
                                content: (
                                    <div className="marketplace-cart-line__preview">
                                        {previewContent}
                                    </div>
                                ),
                            })}
                            <div className="marketplace-cart-line__details">
                                <div className="marketplace-cart-line__name">
                                    {renderAssetAction({
                                        className: 'marketplace-cart-line__name-action',
                                        line,
                                        content: nameContent,
                                    })}
                                </div>
                                <div className="marketplace-cart-line__actions">
                                    <div className="marketplace-cart-line__price">{formatCartLinePrice(line)}</div>
                                    <button
                                        aria-label={`Remove ${displayName} from cart`}
                                        className="marketplace-cart-inline-button marketplace-cart-inline-button--icon"
                                        onClick={() => onRemove(line.lineKey)}
                                        type="button"
                                    >
                                        <TrashIcon />
                                    </button>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
            <div className="marketplace-cart-summary__footer">
                <div className="marketplace-cart-summary__subtotal">
                    <span>Subtotal</span>
                    <strong>{snapshot.subtotal.formatted || 'Pending'}</strong>
                </div>
                {showCheckoutAuthPrompt &&
                <div className="marketplace-cart-auth-prompt">
                    <p>Log in or sign up to continue to checkout.</p>
                    <div className="marketplace-cart-auth-prompt__actions">
                        <a className="marketplace-cart-button is-primary" href={loginHref}>
                            Log in
                        </a>
                        <a className="marketplace-cart-button is-secondary" href={signupHref}>
                            Sign up
                        </a>
                    </div>
                </div>
                }
                <div className="marketplace-cart-summary__actions">
                    {continueExploringHref &&
                    <a className="marketplace-cart-button is-primary" href={continueExploringHref}>
                        Continue exploring
                    </a>
                    }
                    {!continueExploringHref &&
                    <>
                    <button
                        className="marketplace-cart-button is-primary is-checkout"
                        disabled={checkoutPending}
                        onClick={onCheckout}
                        type="button"
                    >
                        Checkout
                    </button>
                    <div className="marketplace-cart-summary__or">or</div>
                    <a className="marketplace-cart-button is-secondary is-subscribe" href={subscribeHref}>
                        <span className="glyphicon glyphicon-star" aria-hidden="true" />
                        <span>{subscribeLabel}</span>
                    </a>
                    <p className="marketplace-cart-summary__subscribe-subtext">{subscribeSubtext}</p>
                    </>
                    }
                </div>
                <div className="marketplace-cart-summary__legal">
                    <a href="https://www.productioncrate.com/terms.html">Terms of Service</a>
                    <span aria-hidden="true">·</span>
                    <a href="https://www.productioncrate.com/privacy.html">Privacy Policy</a>
                </div>
            </div>
        </div>
    );
};

export default MarketplaceCartSummary;
